import { CommonDialogs } from "@mcleod/common";
import {
    Button, ChangeEvent, ClickEvent, DataSourceRemoteDataChangeEvent, Layout, ModelDataChangeType, Snackbar, Table,
    TableSelectionEvent, Toast
} from "@mcleod/components";
import { Api, Logger, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutOrderPayablesToolPanel } from "./autogen/AutogenLayoutOrderPayablesToolPanel";
import { OrderPayables, OrderPayablesTabLayout } from "./OrderPayables";
import { BulkUpdatesConfirmation } from "./BulkUpdatesConfirmation";
import { OrderPayablesProcess } from "./OrderPayablesProcess";
import { ReverseTransferSettlementStepper } from "./ReverseTransferSettlementStepper";
import { OrderPayablesPendingReview } from "./OrderPayablesPendingReview";
import { DriverSettlement, DriverSettlementTabLayout } from "./driver-settlement/DriverSettlement";
import { DriverSettlementProcess } from "./driver-settlement/DriverSettlementProcess";
import { DriverSettlementHold } from "./driver-settlement/DriverSettlementHold";
import { DriverSettlementPendingReview } from "./driver-settlement/DriverSettlementPendingReview";

const log: Logger = LogManager.getLogger("OrderPayablesToolPanel");

export class OrderPayablesToolPanel extends AutogenLayoutOrderPayablesToolPanel {
    private table: Table;

    override onLoad(): void {
        this.addMountListener(() => {
            if (OrderPayables.isOrderPayablesLayoutTab(this.owner) || DriverSettlement.isDriverSettlementLayoutTab(this.owner)) {
                this.initialize(this.owner);
            }

            this.buttonReverseTransfer.visible = true;
        });
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(layout: OrderPayablesTabLayout | DriverSettlementTabLayout) {
        if (OrderPayables.isOrderPayablesLayoutTab(layout)) {
            this.table = (layout as OrderPayablesTabLayout).tableOrderPayables;
        } else {
            this.table = (layout as DriverSettlementTabLayout).tableDriverSettlement;
        }
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => {
                this.tableOnSelect(event);
            });
            this.table?.dataSource?.addRemoteDataChangeListener((event: DataSourceRemoteDataChangeEvent) => this.onRemoteDataChange(event));
        } else {
            this.removeAll();
        }
    }

    onRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (event.change.type === ModelDataChangeType.DELETE) {
            const settlementId = event.change.data.get("record_id");
            if (settlementId != null && this.table.selectedRow != null) {
                const selectedMoveId = this.table.selectedRow.data.get("record_id");
                if (settlementId === selectedMoveId)
                    this.table.selectedRow = null;
            }
        }
    }

    async tableOnSelect(event: TableSelectionEvent) {
        const modelRow = event.newRow?.data;
        await this.syncToolPanel(modelRow);
    }

    public async syncToolPanel(modelRow: ModelRow) {
        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;
            button.enabled = modelRow != null;
            if (button.enabled) {
                this.syncButtonState(button, modelRow);
            }
        });
    }

    private syncButtonState(button: Button, modelRow: ModelRow) {
        if (button === this.buttonUpdateStatuses) {
            const payeeAccepted = !modelRow.isNull("payee_process_status");
            button.enabled = !(payeeAccepted && this.table.parent instanceof DriverSettlementProcess);
            if (!button.enabled) {
                button.disabledTooltip = "Cannot update statuses for payees that have accepted the settlement";
            }
        }
        if (button === this.buttonReverseTransfer) {
            const noAgentsSelected = this.table.selectedRows.every(row => row.data.get("payee_type") !== "A");
            const payeeAccepted = !modelRow.isNull("payee_process_status") && DriverSettlement.isDriverSettlementLayoutTab(this.owner);
            button.enabled = noAgentsSelected && !payeeAccepted;
            if (!button.enabled) {
                if (payeeAccepted) {
                    button.disabledTooltip = "Cannot reverse transfer for payees that have accepted the settlement";
                } else if (!noAgentsSelected) {
                    button.disabledTooltip = "Cannot reverse transfer for agent payables";
                }
            }
        }
    }

    buttonUpdateStatusesOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        const layout = Layout.getLayout("lme/settlement/BulkUpdatesConfirmation") as BulkUpdatesConfirmation;
        const recordCount = selectedRows.length;
        const confirmDialog = CommonDialogs.createDialog(layout, {
            title: `Update Status (${recordCount} ${recordCount > 1 ? "records" : "record"})`,
            okVisible: true
        });
        layout.addLayoutLoadListener(() => {
            if ((this.table.parent instanceof DriverSettlementHold || this.table.parent instanceof DriverSettlementPendingReview) && !this.table.selectedRows[0].data.isNull("payee_process_status")) {
                layout.textboxStatus.removeItemsWithValue("Y");
                layout.textboxStatus.setSelectedItemFromValue("N");
            } else {
                if (this.table.selectedRows.every(row => row.data.get("ready_to_pay_flag") === "Y")) {
                    layout.textboxStatus.setSelectedItemFromValue("N");
                } else {
                    layout.textboxStatus.setSelectedItemFromValue("Y");
                }
            }
            layout.textboxStatus.addAfterDropdownSelectionListener(() => {
                layout.checkboxConfirmVoid.enabled = layout.textboxStatus.valueAsString == "V";
                if (layout.checkboxConfirmVoid.enabled) {
                    confirmDialog.setOkButtonProps({ enabled: false });
                } else {
                    confirmDialog.setOkButtonProps({ enabled: true });
                }

            });
            layout.checkboxConfirmVoid.addChangeListener((event: ChangeEvent) => {
                confirmDialog.setOkButtonProps({ enabled: event.newValue });
            });
        });

        confirmDialog.show().then(() => Promise.resolve(confirmDialog).then(async result => {
            if (!result.wasCancelled) {
                const reasonCodeID = layout.textboxReasonCode.lookupModelData != null ? layout.textboxReasonCode.lookupModelData[0].get("id") : null;
                const settlementRows = selectedRows.filter(row => row.data.get("record_type") === "S");
                const selectedPayAdjustmentRows = selectedRows.filter(row => row.data.get("record_type") === "P");
                const payAdjustmentRows = [];
                settlementRows.forEach(settlementRow => {
                    payAdjustmentRows.push(...this.table.allRows.filter(row => row.data.get("record_type") === "P" && row.data.get("order_id") === settlementRow.data.get("order_id") && row.data.get("movement_id") === settlementRow.data.get("movement_id") && row.data.get("payee_id") === settlementRow.data.get("payee_id")));
                });
                selectedPayAdjustmentRows.forEach(selectedPayAdjustmentRow => {
                    if (payAdjustmentRows.includes(selectedPayAdjustmentRow)) {
                        payAdjustmentRows.splice(payAdjustmentRows.indexOf(selectedPayAdjustmentRow), 1);
                    }
                });
                Api.post("lme/settlement/update-statuses", {
                    status: layout.textboxStatus.selectedItem.value,
                    settlement_ids: settlementRows.map(row => row.data.get("record_id")).join(","),
                    pay_adjustment_ids: payAdjustmentRows.map(row => row.data.get("record_id")).join(","),
                    selected_pay_adjustment_ids: selectedPayAdjustmentRows.map(row => row.data.get("record_id")).join(","),
                    reason_code: reasonCodeID,
                    reason_comment: layout.textboxComment.text,
                    associate_pay_adjustments: layout.checkboxAssociatePayAdjustments.checked
                }).then((response) => {
                    const data = response?.data[0];
                    if (data == null) {
                        log.error("No data returned from update-statuses API call.");
                        return;
                    }
                    if (!StringUtil.isEmptyString(data?.error_reasons)) {
                        CommonDialogs.showError(data.error_reasons, `${data.error_count} record${data.error_count > 1 ? "s" : ""} failed to update.`);
                    } else {
                        Toast.showSuccessToast(`Updated ${data.num_records_processed} record${data.num_records_processed > 1 ? "s" : ""} to ${layout.textboxStatus.selectedItem.displayValue} status.`);

                        if (this.table.parent instanceof OrderPayablesProcess) {
                            (this.table.parent as OrderPayablesProcess).resetProcessButtonCaption();
                        }
                    }
                    this.table.selectedRows = null;
                });
            }
        }));
    }

    buttonReverseTransferOnClick(event: ClickEvent) {
        this.reverseTransferToSettlement(event.target as Button);
    }

    buttonReadyToSubmitOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        if (selectedRows.length === 0) {
            Snackbar.showWarningSnackbar("Please select at least one row to submit.");
            return;
        }
        const selectedRowData: ModelRow[] = selectedRows.map(row => row.data);
        if (this.table.parent instanceof OrderPayablesPendingReview) {
            const layoutOrderPayablesPendingReview = this.table.parent as OrderPayablesPendingReview;
            layoutOrderPayablesPendingReview.openTriumphPaySubmissionSlideout(selectedRowData);
        }

    }

    private reverseTransferToSettlement(buttonReverseTransfer?: Button, onCancel?: () => void) {
        const selectedRows = this.table.selectedRows;

        const selectedRowData = [];
        const movementIDs = new Set();
        selectedRows.forEach(row => {
            movementIDs.add(row.data.get("movement_id"));
            selectedRowData.push(row.data.data);
        });
        const movementIDArray = Array.from(movementIDs);

        this.buttonReverseTransfer.busy = true;
        ReverseTransferSettlementStepper.showAsSlideout({
            selectedRows: selectedRowData,
            processCount: movementIDArray.length,
            onClose: (canceled) => {
                if (!canceled) {
                } else if (onCancel) {
                    onCancel();
                }
            }
        }).finally(() => {
            this.buttonReverseTransfer.busy = false;
        });
    }
}

